import { useId, useState, type FormEvent } from "react";

import { useHealth } from "@/api";
import { K60Logo } from "@/brand/K60Logo";
import { Button } from "@/components/Button";
import { MeshField } from "@/components/MeshField";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

import styles from "./LoginScreen.module.css";

export interface Credentials {
  email: string;
  password: string;
}

interface LoginScreenProps {
  /**
   * Microsoft Entra ID — the primary way in once it is wired (open point 8,
   * decided 30.08.2026). Without a handler the screen offers no Microsoft
   * button at all: a first button that only answers „noch nicht angebunden"
   * is a dead end on the door (Welle 0, S2, 18.10.2026).
   */
  onMicrosoft?: () => void;
  /** Local credentials — the fallback. Rejects with a message the form shows verbatim. */
  onCredentials: (credentials: Credentials) => Promise<void> | void;
  /**
   * A message about the Microsoft way in — not caused by the local form, so
   * not part of its own `error` state, but shown through the same inline
   * affordance (same role, same styling, its own id) rather than a second
   * kind of message the screen would need its own voice for.
   */
  notice?: string | null;
}

/** The four-square mark. Microsoft's own colours; it is their sign, not ours. */
function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
      <rect x="1" y="1" width="9" height="9" className={styles.microsoftRed} />
      <rect x="11" y="1" width="9" height="9" className={styles.microsoftGreen} />
      <rect x="1" y="11" width="9" height="9" className={styles.microsoftBlue} />
      <rect x="11" y="11" width="9" height="9" className={styles.microsoftYellow} />
    </svg>
  );
}

function BuildLine() {
  const { data, isPending, isError } = useHealth();
  const state = isPending ? "pending" : isError ? "down" : "up";
  const text =
    state === "up" ? `Build ${data?.git_sha ?? "unbekannt"}` : state === "down" ? "API nicht erreichbar" : "API wird geprüft";

  return (
    <p className={styles.build}>
      <span className={`${styles.dot} ${styles[state]}`} aria-hidden="true" />
      {text}
    </p>
  );
}

/**
 * Sign-in.
 *
 * Two ways in, in this order: the Microsoft account first, because that is
 * where K60's users already are and where IT holds the keys; local
 * credentials second, for the people who have no account there. Nothing on
 * this screen is data, so the brand may speak at full volume — the mesh on
 * the left, red on the one primary action — and nothing here uses the status
 * palette. A failed attempt is said in words with a graphite edge, not in
 * red: red on this screen would be the brand's, and a rule violation it is not.
 */
export function LoginScreen({ onMicrosoft, onCredentials, notice = null }: LoginScreenProps) {
  useDocumentTitle("Anmelden");

  const emailId = useId();
  const passwordId = useId();
  const errorId = useId();
  const noticeId = useId();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.trim() || !password) return;

    setPending(true);
    setError(null);
    try {
      await onCredentials({ email: email.trim(), password });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Anmeldung fehlgeschlagen.");
      setPassword("");
    } finally {
      setPending(false);
    }
  }

  return (
    <main className={styles.screen}>
      <aside className={styles.panel} aria-hidden="true">
        {/* The door's entrance: the grating is laid once, then it stands. */}
        <MeshField className={styles.mesh} settle />
        <p className={styles.claim}>Von Norm bis Sonderbau</p>
      </aside>

      <section className={styles.side}>
        <div className={styles.card}>
          <span className={styles.nameplate}>
            <K60Logo height={56} />
          </span>
          <p className={styles.product}>Werkbank Hausprojekte</p>

          <h1 className={styles.title}>Anmelden</h1>
          <p className={styles.subline}>Zeichnung rein, Stückliste raus.</p>

          {onMicrosoft ? (
            <button type="button" className={styles.microsoft} onClick={onMicrosoft}>
              <MicrosoftLogo />
              Mit Microsoft anmelden
            </button>
          ) : null}

          {notice ? (
            <p id={noticeId} className={styles.error} role="alert">
              {notice}
            </p>
          ) : null}

          {onMicrosoft ? (
            <p className={styles.or}>
              <span>oder mit Zugangsdaten</span>
            </p>
          ) : null}

          <form className={styles.form} onSubmit={submit} noValidate>
            <div className={styles.field}>
              <label htmlFor={emailId} className="label">
                E-Mail
              </label>
              <input
                id={emailId}
                className={styles.input}
                type="email"
                name="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-describedby={error ? errorId : undefined}
                aria-invalid={error ? true : undefined}
                required
              />
            </div>

            <div className={styles.field}>
              <div className={styles.fieldHead}>
                <label htmlFor={passwordId} className="label">
                  Passwort
                </label>
                <button
                  type="button"
                  className={styles.reveal}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-pressed={showPassword}
                  aria-controls={passwordId}
                >
                  {showPassword ? "Verbergen" : "Anzeigen"}
                </button>
              </div>
              <input
                id={passwordId}
                className={styles.input}
                type={showPassword ? "text" : "password"}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-describedby={error ? errorId : undefined}
                aria-invalid={error ? true : undefined}
                required
              />
            </div>

            {error ? (
              <p id={errorId} className={styles.error} role="alert">
                {error}
              </p>
            ) : null}

            {/* `busy` is the visible part (colours kept, cursor); `disabled` as well,
                because a sign-in must not fire twice. */}
            <Button type="submit" variant="signal" size="l" busy={pending} disabled={pending} className={styles.submit}>
              {pending ? "Meldet an …" : "Anmelden"}
            </Button>
          </form>

          <p className={styles.hint}>Kein Zugang? Zugänge richtet aivori ein.</p>
        </div>

        <footer className={styles.foot}>
          <BuildLine />
          {/* Was „Verarbeitung in der EU" — no longer true for the reading
              since E-59 (15.10.2026: OpenAI on the global host). What holds
              is where our data lives: the KVM2 in Frankfurt am Main
              (daten-und-freigaben.md, „Speicherort"). */}
          <p className={styles.legal}>Speicherort Frankfurt am Main</p>
        </footer>
      </section>
    </main>
  );
}
